import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentBacktestsSummary, getHomeMarketNews, getHomeMarketOverview, getHomeMarketRegime, getHomeMarketInsight } from '../api';
import Sparkline from '../components/Sparkline';

function HeroCard({ title, desc, accent, onClick }) {
  return (
    <button
      className="home-hero-card"
      onClick={onClick}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 12,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))',
        color: 'var(--text)',
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%',
        padding: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: accent,
          flexShrink: 0,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)',
        }} />
        <div>
          <div className="home-hero-card-title" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>{desc}</div>
        </div>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 20 }}>→</div>
    </button>
  );
}

function prettyTimeframe(bt) {
  const tf = bt?.fx_interval || bt?.interval || '1d';
  const map = {
    '1d': '1D', '1h': '1H', '4h': '4H', '30m': '30M', '15m': '15M', '5m': '5M',
  };
  return map[tf] || String(tf).toUpperCase();
}

export default function HomePage() {
  const navigate = useNavigate();
  const [recentBacktests, setRecentBacktests] = useState([]);
  const [loadingBacktests, setLoadingBacktests] = useState(true);
  const [newsItems, setNewsItems] = useState([]);
  const [newsLastUpdated, setNewsLastUpdated] = useState(null);
  const [marketOverview, setMarketOverview] = useState([]);
  const [marketOverviewUpdatedAt, setMarketOverviewUpdatedAt] = useState(null);
  const [marketOverviewError, setMarketOverviewError] = useState('');
  const [loadingMarketOverview, setLoadingMarketOverview] = useState(true);
  const [regimePayload, setRegimePayload] = useState(null);
  const [loadingRegime, setLoadingRegime] = useState(true);
  const [insightItem, setInsightItem] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoadingBacktests(true);
    getRecentBacktestsSummary()
      .then((res) => {
        if (!mounted) return;
        setRecentBacktests((res.data || []).slice(0, 5));
      })
      .catch(() => {
        if (!mounted) return;
        setRecentBacktests([]);
      })
      .finally(() => {
        if (mounted) setLoadingBacktests(false);
      });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    getHomeMarketNews(6)
      .then((res) => {
        if (!mounted) return;
        const items = res.data?.items || [];
        setNewsItems(items);
        setNewsLastUpdated(res.data?.last_updated || null);
      })
      .catch(() => {
        if (!mounted) return;
        setNewsItems([]);
        setNewsLastUpdated(null);
      });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadOverview = () => {
      setLoadingMarketOverview(true);
      getHomeMarketOverview()
        .then((res) => {
          if (!mounted) return;
          setMarketOverview(res.data?.rows || []);
          setMarketOverviewUpdatedAt(res.data?.as_of || new Date().toISOString());
          setMarketOverviewError('');
        })
        .catch(() => {
          if (!mounted) return;
          setMarketOverview([]);
          setMarketOverviewUpdatedAt(null);
          setMarketOverviewError('Live market data is temporarily unavailable.');
        })
        .finally(() => {
          if (mounted) setLoadingMarketOverview(false);
        });
    };

    loadOverview();
    const timer = window.setInterval(loadOverview, 5 * 60 * 1000);

    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoadingRegime(true);
    getHomeMarketRegime(365)
      .then((res) => {
        if (!mounted) return;
        setRegimePayload(res.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setRegimePayload(null);
      })
      .finally(() => {
        if (mounted) setLoadingRegime(false);
      });

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    getHomeMarketInsight()
      .then((res) => {
        if (!mounted) return;
        setInsightItem(res.data?.item || null);
      })
      .catch(() => {
        if (!mounted) return;
        setInsightItem(null);
      });

    return () => { mounted = false; };
  }, []);

  const topNews = newsItems[0] || null;
  const regimeSummary = regimePayload?.summary || null;
  const regimeFamilies = regimePayload?.families || {};
  const conciseRegimeText = [
    regimeFamilies.growth_inflation?.current?.regime,
    regimeFamilies.volatility_risk?.current?.regime,
    regimeFamilies.liquidity?.current?.regime,
  ].filter(Boolean).join(' · ');

  const relativeAge = (isoDate) => {
    if (!isoDate) return '';
    const ms = Date.now() - new Date(isoDate).getTime();
    const mins = Math.max(1, Math.floor(ms / 60000));
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const formatMarketValue = (row) => {
    if (row?.error) return 'Unavailable';
    if (row?.display_mode === 'percent') {
      return row?.current_value == null ? '—' : `${Number(row.current_value).toFixed(2)}%`;
    }
    return row?.current_value == null ? '—' : Number(row.current_value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatMarketChange = (row) => {
    if (row?.error || row?.change_pct == null) return '—';
    const change = Number(row.change_pct);
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  };

  return (
    <div className="home-page" style={{ padding: '26px 26px 30px', maxWidth: 1360, margin: '0 auto' }}>
      <div className="home-header-row" style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div>
          {/*
          <h1 style={{ margin: 0, fontSize: 36, lineHeight: 1.05, letterSpacing: -0.4 }}>
            {greetingForHour(new Date().getHours())}, {user?.username || 'Trader'}
          </h1>
          */}
          <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 14 }}>
            Here is what is happening in the markets today.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/*
          <div style={{
            minWidth: 360,
            maxWidth: 540,
            width: 'min(54vw, 540px)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ color: 'var(--muted)' }}>⌕</span>
            <input
              aria-label="Search"
              placeholder="Search markets, strategies, or data..."
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                background: 'transparent',
                color: 'var(--text)',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
              }}
            />
            <span style={{ color: 'var(--muted)', fontSize: 11 }}>Ctrl K</span>
          </div>
          */}

          <button
            className="home-new-btn"
            onClick={() => navigate('/builder')}
            style={{
              border: '1px solid #4f46e5',
              background: 'linear-gradient(180deg, #5b54ff, #4f46e5)',
              color: '#fff',
              borderRadius: 10,
              padding: '9px 14px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            + New
          </button>
        </div>
      </div>

      <div className="home-card" style={{ marginTop: 22, border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: 16 }}>
        <h2 className="home-card-title" style={{ margin: 0, fontSize: 24 }}>What would you like to do?</h2>
        <div className="home-hero-grid" style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          <HeroCard
            title={loadingRegime ? 'Regime Tracker' : 'Regime Tracker'}
            desc={loadingRegime ? 'Loading macro snapshot...' : (conciseRegimeText || regimeSummary?.description || 'View regime history.')}
            accent="linear-gradient(180deg, #0ea5e9, #0284c7)"
            onClick={() => navigate('/regime-tracker')}
          />
          <HeroCard
            title="AI Research Analyst"
            desc="Ask anything about markets, strategies, or data. Get AI-powered insights."
            accent="linear-gradient(180deg, #6d28d9, #4c1d95)"
            onClick={() => navigate('/analyst')}
          />
          <HeroCard
            title="Run a Backtest"
            desc="Test your strategy ideas with historical data and performance metrics."
            accent="linear-gradient(180deg, #16a34a, #166534)"
            onClick={() => navigate('/backtest')}
          />
          <HeroCard
            title="Market News"
            desc={topNews ? topNews.title : 'Stay up to date with the latest market news and insights.'}
            accent="linear-gradient(180deg, #2563eb, #1d4ed8)"
            onClick={() => {
              if (topNews?.url) window.open(topNews.url, '_blank', 'noopener,noreferrer');
            }}
          />
        </div>
      </div>

      <div className="home-grid-main" style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <section className="home-card" style={{ border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: 16 }}>
          <div className="home-section-title" style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>Market Overview</div>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
            Live market data refreshed throughout the day.
          </div>
          {loadingMarketOverview && marketOverview.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>Loading live market data...</div>
          )}
          {!loadingMarketOverview && marketOverview.length === 0 && marketOverviewError && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>{marketOverviewError}</div>
          )}
          {marketOverview.map((row) => (
            <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{row.label}</div>
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>{row.symbol}</div>
              </div>
              <Sparkline points={row.points || []} times={row.times} color={row.color || '#22c55e'} expectedPoints={row.expected_points} displayMode={row.display_mode} />
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{formatMarketValue(row)}</div>
                <div style={{ color: row?.change_pct >= 0 ? '#22c55e' : '#ef4444', fontSize: 12 }}>{formatMarketChange(row)}</div>
              </div>
            </div>
          ))}
          {marketOverviewUpdatedAt && (
            <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 11 }}>
              Updated {relativeAge(marketOverviewUpdatedAt)}
            </div>
          )}
          <button onClick={() => navigate('/markets')} style={{ marginTop: 8, background: 'none', border: 'none', color: '#7c8cff', cursor: 'pointer', padding: 0 }}>View all markets →</button>
        </section>

        <section className="home-card" style={{ border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: 16 }}>
          <div className="home-section-title" style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>Latest News</div>
          {newsItems.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>No cached news yet. It will populate after the hourly sync.</div>
          )}
          {newsItems.slice(0, 4).map((item) => (
            <div
              key={item.id || item.url}
              style={{ padding: '12px 0', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', gap: 10, cursor: item.url ? 'pointer' : 'default' }}
              onClick={() => {
                if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer');
              }}
            >
              <div style={{ fontSize: 13 }}>{item.title}</div>
              <div style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{relativeAge(item.published_at)}</div>
            </div>
          ))}
          {newsLastUpdated && (
            <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 11 }}>
              Updated {relativeAge(newsLastUpdated)}
            </div>
          )}
        </section>

        <section className="home-card" style={{ border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: 16 }}>
          <div className="home-section-title" style={{ fontSize: 28, fontWeight: 700, marginBottom: 12 }}>AI Insight of the Day</div>
          <p style={{ margin: 0, color: 'var(--muted)', lineHeight: 1.7, fontSize: 14 }}>
            {insightItem?.insight_text || 'No AI insight has been generated yet. Check back after the next scheduled update at 8:00 AM ET or 5:00 PM ET.'}
          </p>
          {insightItem?.created_at && (
            <div style={{ marginTop: 10, color: 'var(--muted)', fontSize: 11 }}>
              Updated {relativeAge(insightItem.created_at)}
            </div>
          )}
          <button onClick={() => navigate('/analyst')} style={{ marginTop: 20, background: 'none', border: 'none', color: '#7c8cff', cursor: 'pointer', padding: 0 }}>Ask AI Research Analyst →</button>
        </section>
      </div>

      <section className="home-card" style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="home-section-title" style={{ fontSize: 28, fontWeight: 700 }}>Recent Backtests</div>
          <button onClick={() => navigate('/backtests')} style={{ background: 'none', border: 'none', color: '#7c8cff', cursor: 'pointer', padding: 0 }}>View all backtests →</button>
        </div>

        <div className="home-table-wrap" style={{ marginTop: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead>
              <tr style={{ color: 'var(--muted)', fontSize: 11, textAlign: 'left' }}>
                <th style={{ padding: '8px 10px' }}>Strategy</th>
                <th style={{ padding: '8px 10px' }}>Asset</th>
                <th style={{ padding: '8px 10px' }}>Timeframe</th>
                <th style={{ padding: '8px 10px' }}>Return</th>
                <th style={{ padding: '8px 10px' }}>Sharpe</th>
                <th style={{ padding: '8px 10px' }}>Max Drawdown</th>
                <th style={{ padding: '8px 10px' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {loadingBacktests && (
                <tr>
                  <td colSpan={7} style={{ padding: '14px 10px', color: 'var(--muted)' }}>Loading recent backtests...</td>
                </tr>
              )}

              {!loadingBacktests && recentBacktests.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '14px 10px', color: 'var(--muted)' }}>
                    No backtests yet. Run your first one from the Backtest page.
                  </td>
                </tr>
              )}

              {!loadingBacktests && recentBacktests.map((bt) => {
                const ret = Number(bt?.return_pct ?? 0);
                const dd = Number(bt?.max_drawdown_pct ?? 0);
                const sharpe = bt?.sharpe_ratio;
                return (
                  <tr
                    key={bt.id}
                    onClick={() => navigate(`/backtests/${bt.id}`)}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '10px', fontWeight: 600 }}>{bt?.strategy_name || 'Untitled Strategy'}</td>
                    <td style={{ padding: '10px' }}>{String(bt?.asset_type || '').toUpperCase()}</td>
                    <td style={{ padding: '10px' }}>{prettyTimeframe(bt)}</td>
                    <td style={{ padding: '10px', color: ret >= 0 ? '#22c55e' : '#ef4444' }}>{ret.toFixed(2)}%</td>
                    <td style={{ padding: '10px' }}>{sharpe == null ? '-' : Number(sharpe).toFixed(2)}</td>
                    <td style={{ padding: '10px', color: dd <= 0 ? '#ef4444' : 'var(--text)' }}>{dd.toFixed(2)}%</td>
                    <td style={{ padding: '10px', color: 'var(--muted)' }}>{new Date(bt.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        @media (max-width: 1120px) {
          .home-grid-main { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
